/* eslint-disable no-plusplus */
function maybeReplace(format, pattern, replacementFn) {
  return format.replace(pattern, (match, lookbehind) => {
    // https://caniuse.com/#search=lookbehind
    if (lookbehind === '%') {
      return match;
    }
    const replacement = replacementFn();
    if (replacement === undefined) {
      return match;
    }
    return replacement;
  });
}

/**
 * Simple function for formatting strings. Replaces placeholders with values passed as extra
 * arguments. The following placeholders are supported:
 *
 *  - %s: renders value as String
 *  - %j: renders value as JSON
 *  - %%: renders percentage symbol (escape sequence)
 *
 * See: https://nodejs.org/api/util.html#util_util_format_format_args
 */
function sprintf(format) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }
  const argsLength = args.length;
  if (argsLength === 0) {
    return format;
  }
  let index = 0;
  const withString = maybeReplace(format, /(?<lookbehind>%)?%s/g, () => {
    return index >= argsLength ? undefined : String(args[index++]);
  });
  let withJSON = maybeReplace(withString, /(?<lookbehind>%)?%j/g, () => {
    return index >= argsLength ? undefined : String(JSON.stringify(args[index++], getCircularReplacer()));
  });
  for (index; index < argsLength; index++) {
    withJSON += ` ${String(args[index])}`;
  }
  return withJSON.replace(/%%/g, '%');
}
function getCircularReplacer() {
  const seen = new WeakSet();
  return (_, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }
      seen.add(value);
    }
    return value;
  };
}

/**
 * Use invariant() to assert state which your program assumes to be true.
 * It is intended to indicate a programmer error for a condition that should
 * never occur.
 *
 * Provide sprintf-style format (only %s is supported) and arguments to provide
 * information about what broke and what you were expecting.
 *
 */
function invariant(condition, format) {
  if (process.env.NODE_ENV !== "production") {
    if (format === undefined) {
      throw new Error('invariant(...): Second argument must be a string.');
    }
  }
  if (!condition) {
    let error;
    if (format === undefined) {
      error = new Error('Minified exception occurred; use the non-minified dev environment ' + 'for the full error message and additional helpful warnings.');
    } else {
      for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
        args[_key - 2] = arguments[_key];
      }
      error = new Error(sprintf(format, ...args));
      error.name = 'Invariant Violation';
    }
    throw error;
  }
}

// https://stackoverflow.com/questions/17575790/environment-detection-node-js-or-browser/31090240#31090240
// eslint-disable-next-line no-new-func
const testIsBrowser = new Function('try {return this===window;}catch(e){ return false;}');
function isBrowser() {
  return testIsBrowser();
}

function isNumeric(value) {
  // @ts-ignore: TS error is what we are trying to check
  return !isNaN(parseFloat(value)) && isFinite(value);
}

function isObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isObjectEmpty(obj) {
  // https://stackoverflow.com/a/32108184/3135248

  return isObject(obj) && Object.entries(obj).length === 0 && Object.getOwnPropertyNames(obj).length === 0 && obj.constructor === Object;
}

// This is essentially a special case of `invariant` for null values.
function nullthrows(x) {
  let message = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'Got unexpected null or undefined.';
  if (x == null) {
    throw new Error(message);
  }
  return x;
}

/**
 * Similar to invariant but only logs a warning if the condition is not met.
 * This can be used to log issues in development environments in critical
 * paths. Removing the logging code for production environments will keep the
 * same logic and follow the same code paths.
 */
function printWarning(format) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }
  const message = sprintf(format, ...args);
  if (typeof console !== 'undefined') {
    console.warn(message); // eslint-disable-line no-console
  }
}

function warning(condition, format) {
  if (process.env.NODE_ENV !== "production") {
    if (format === undefined) {
      throw new Error('`warning(condition, format, ...args)` requires a warning message argument');
    }
    if (!condition) {
      for (var _len2 = arguments.length, args = new Array(_len2 > 2 ? _len2 - 2 : 0), _key2 = 2; _key2 < _len2; _key2++) {
        args[_key2 - 2] = arguments[_key2];
      }
      printWarning(format, ...args);
    }
  }
}

export { invariant, isBrowser, isNumeric, isObject, isObjectEmpty, nullthrows, sprintf, warning };
